import { forwardRef } from '@chakra-ui/react';
import type { HTMLChakraProps } from '@chakra-ui/react';
import { chakra, useMultiStyleConfig } from '@chakra-ui/system';
import { cx } from '@chakra-ui/utils';

import { Skeleton } from '@frontend/components';

import { PercentageBreakdownBarSegment } from './PercentageBreakdownBarSegment';
import { usePercentageBreakdownContext } from './context';

export type PercentageBreakdownBarProps = HTMLChakraProps<'div'>;

/**
 * PercentageBreakdownBar displays a horizontal bar with color-coded segments
 * representing the data from the PercentageBreakdown context.
 */
export const PercentageBreakdownBar = forwardRef<
  PercentageBreakdownBarProps,
  'div'
>(({ className, ...props }, ref) => {
  const { data, size, variant, maxTotal } = usePercentageBreakdownContext();
  const styles = useMultiStyleConfig('PercentageBreakdown', { size, variant });
  const _className = cx('percentage-breakdown__bar', className);

  const nonZeroItems = data.filter((item) => item.value > 0);

  const barSegments = nonZeroItems.map((item) => (
    <PercentageBreakdownBarSegment
      key={`${item.label}-${item.value}-${item.color}`}
      bg={item.color}
      flexGrow={item.value}
      {...(item.tiled && {
        position: 'relative',
        sx: {
          _after: {
            content: '""',
            position: 'absolute',
            inset: '1px',
            backgroundImage:
              'repeating-linear-gradient(135deg, transparent, transparent 1px, rgba(255,255,255,0.8) 1px, rgba(255,255,255,0.8) 3px)',
          },
        },
      })}
    />
  ));

  const total = nonZeroItems.reduce((sum, item) => sum + item.value, 0);

  const barWidthPercent =
    variant === 'count' && maxTotal && maxTotal > 0
      ? `${Math.min((total / maxTotal) * 100, 100)}%`
      : undefined;

  return (
    <chakra.div
      display="flex"
      alignItems="center"
      width="100%"
      {...props}
      className={_className}
      ref={ref}
    >
      <chakra.div
        __css={styles.bar}
        width={barWidthPercent}
        flexGrow={barWidthPercent ? 0 : 1}
      >
        {barSegments.length > 0 ? (
          barSegments
        ) : (
          <Skeleton flexGrow={1}>
            <PercentageBreakdownBarSegment width="100%" />
          </Skeleton>
        )}
      </chakra.div>
      <chakra.span __css={styles.barTotal} marginLeft="auto">
        {total}
      </chakra.span>
    </chakra.div>
  );
});
PercentageBreakdownBar.displayName = 'PercentageBreakdownBar';
